import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Tooltip,
  CircularProgress, Stack, Divider, Checkbox, useTheme
} from '@mui/material';
import {
  Business as CompanyIcon, Phone as PhoneIcon, Email as EmailIcon,
  CalendarToday as CalendarIcon, Person as PersonIcon, Unarchive as UnarchiveIcon,
  Delete as DeleteIcon, ViewKanban as BoardIcon, Notes as NotesIcon
} from '@mui/icons-material';
import Layout from '../components/Layout';

const Archives = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArchivedCards();
  }, []);

  const fetchArchivedCards = async () => {
    try {
      const response = await axios.get('/api/cards/archived/all');
      setCards(response.data);
    } catch (error) {
      console.error('Ошибка загрузки архивных карточек:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (cardId) => {
    try {
      await axios.patch(`/api/cards/${cardId}/unarchive`);
      setCards(cards.filter(c => c._id !== cardId));
    } catch (error) {
      console.error('Ошибка восстановления карточки:', error);
    }
  };

  const handleDelete = async (cardId) => {
    if (window.confirm('Удалить карточку навсегда? Это действие нельзя отменить.')) {
      try {
        await axios.delete(`/api/cards/${cardId}`);
        setCards(cards.filter(c => c._id !== cardId));
      } catch (error) {
        console.error('Ошибка удаления карточки:', error);
      }
    }
  };

  const priorityColors = {
    'низкий': '#a3be8c',
    'средний': '#ebcb8b',
    'высокий': '#d08770',
    'критический': '#bf616a',
  };

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} thickness={2} />
          <Typography variant="body2" color="text.secondary">
            Загрузка архива...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800,
            mb: 1,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #e6edf3 0%, #88c0d0 100%)'
              : 'linear-gradient(135deg, #2e3440 0%, #5e81ac 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          📦 Архивы
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Всего архивных карточек: {cards.length}
        </Typography>

        {cards.length === 0 ? (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 8,
              background: isDarkMode ? 'rgba(22, 27, 34, 0.5)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 3,
              border: '1px dashed',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              🗃️ Нет архивных карточек
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Архивированные карточки появятся здесь
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {cards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} key={card._id}>
                <Card
                  sx={{
                    background: isDarkMode 
                      ? 'rgba(22, 27, 34, 0.7)' 
                      : '#ffffff',
                    borderLeft: `4px solid ${priorityColors[card.priority]}`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    animation: `fadeInUp 0.4s ease ${index * 0.05}s both`,
                    '@keyframes fadeInUp': {
                      '0%': { opacity: 0, transform: 'translateY(20px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700} color="text.primary">
                        {card.title}
                      </Typography>
                      <Box>
                        <Tooltip title="Восстановить">
                          <IconButton 
                            size="small" 
                            onClick={() => handleUnarchive(card._id)} 
                            sx={{ color: '#a3be8c' }}
                          >
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить навсегда">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(card._id)} 
                            sx={{ color: '#bf616a' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {card.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {card.description}
                      </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BoardIcon fontSize="small" sx={{ color: '#88c0d0' }} />
                        <Typography variant="caption" fontWeight={600} color="text.primary">
                          {card.board?.name}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CompanyIcon fontSize="small" sx={{ color: '#5e81ac' }} />
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {card.company?.name}
                        </Typography>
                      </Box>

                      {card.company?.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" sx={{ color: '#a3be8c' }} />
                          <Typography variant="caption" color="text.secondary">
                            {card.company.phone}
                          </Typography>
                        </Box>
                      )}

                      {card.company?.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" sx={{ color: '#ebcb8b' }} />
                          <Typography variant="caption" color="text.secondary">
                            {card.company.email}
                          </Typography>
                        </Box>
                      )}

                      {card.archivedAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon fontSize="small" sx={{ color: '#b48ead' }} />
                          <Typography variant="caption" color="text.secondary">
                            Архивировано: {new Date(card.archivedAt).toLocaleDateString('ru-RU')}
                          </Typography>
                        </Box>
                      )}

                      {card.assignedTo && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" sx={{ color: '#b48ead' }} />
                          <Typography variant="caption" color="text.secondary">
                            {card.assignedTo.firstName} {card.assignedTo.lastName}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Стикеры */}
                    {card.stickers && card.stickers.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                        {card.stickers.map((sticker) => (
                          <Chip
                            key={sticker._id}
                            label={sticker.text}
                            size="small"
                            sx={{
                              background: `${sticker.color}30`,
                              border: `1px solid ${sticker.color}`,
                              color: sticker.color,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 24,
                            }}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Задачи */}
                    {card.tasks && card.tasks.length > 0 && (
                      <Box 
                        sx={{ 
                          mt: 2, 
                          p: 1.5, 
                          background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0,0,0,0.03)', 
                          borderRadius: 2 
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Задачи ({card.tasks.filter(t => t.completed).length}/{card.tasks.length})
                        </Typography>
                        {card.tasks.map((task) => (
                          <Box key={task._id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Checkbox
                              size="small"
                              checked={task.completed}
                              disabled
                              sx={{ 
                                p: 0,
                                color: '#88c0d0',
                                '&.Mui-checked': { color: '#a3be8c' }
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                flex: 1,
                                textDecoration: task.completed ? 'line-through' : 'none',
                                color: task.completed ? 'text.secondary' : 'text.primary'
                              }}
                            >
                              {task.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Заметки */}
                    {card.notes && card.notes.length > 0 && (
                      <Box 
                        sx={{ 
                          mt: 2, 
                          p: 1.5, 
                          background: 'rgba(235, 203, 139, 0.1)', 
                          borderRadius: 2, 
                          borderLeft: '3px solid #ebcb8b' 
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: '#ebcb8b' }}>
                          Заметки ({card.notes.length})
                        </Typography>
                        {card.notes.map((note) => (
                          <Box key={note._id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                            <NotesIcon sx={{ fontSize: 14, color: '#ebcb8b', mt: 0.2 }} />
                            <Typography variant="caption" sx={{ flex: 1, color: 'text.primary' }}>
                              {note.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={card.priority}
                        size="small"
                        sx={{
                          background: `${priorityColors[card.priority]}30`,
                          border: `1px solid ${priorityColors[card.priority]}`,
                          color: priorityColors[card.priority],
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Layout>
  );
};

export default Archives;
